import { CheckCheck, Eye, EyeClosed, Flag, FlagOff, Hash, Lock, Plus, RotateCcw } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { SheetHeader } from 'src/components/ui/sheet';
import { Invoice, SKU, Terms } from 'src/redux/billing/billing.types';
import { Company } from 'src/pages/billing/types/billing.types';
import { computeInvoiceDrawerTotalDue, formatCurrency, formatDateTimeVerbose, isLockedInvoiceStatus } from '../../utils/billing-utils';
import StatusBadgeThin from './status-badge-thin';
import BorderedSelect from 'src/components/form-new/bordered-select';
import BorderDateSelect from 'src/components/form-new/border-date-select';
import { Button } from 'src/components/ui/button';
import { useCreateInvoiceLineItemMutation, useResetInvoiceMutation } from 'src/redux/billing/billing.endpoints';
import notificationService from 'src/services/notification/notification.service';
import { useDispatch } from 'react-redux';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/components/ui/tooltip';
import BulkApproveByPlatformModal, {
  BULK_APPROVE_EMPTY_PLATFORM_VALUE,
  type BulkApprovePlatformOption,
} from './bulk-approve-by-platform-modal';

interface InvoiceDrawerHeaderProps {
  isLocked: boolean;
  invoice: Invoice | null | undefined;
  companies: Company[] | null | undefined;
  terms: Terms[] | null | undefined;
  skus?: SKU[] | null;
  refetchInvoiceGroup: () => void;
  updateInvoice: (invoiceId: string, field: string, value: any) => void;
  bulkApproveInvoiceLinesByPlatform?: (platformKey: string) => Promise<void>;
}

const InvoiceDrawerHeader: React.FC<InvoiceDrawerHeaderProps> = ({
  isLocked,
  invoice,
  companies,
  terms,
  skus,
  refetchInvoiceGroup,
  updateInvoice,
  bulkApproveInvoiceLinesByPlatform,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
  const [createLineItem, { isLoading: isCreatingLineItem }] = useCreateInvoiceLineItemMutation();
  const dispatch = useDispatch();

  const [resetInvoice, { isLoading: isResettingInvoice }] = useResetInvoiceMutation();

  const handleResetInvoice = async() => {
    try {
      setIsLoading(true);
      await resetInvoice({ id: invoice?.id || '' }).unwrap();
      handleRefetchInvoiceGroup();
      setIsLoading(false);
      notificationService.handleSuccess('Invoice reset successfully', dispatch, 'Invoice reset successfully');
    } catch (error) {
      console.error('Failed to reset invoice:', error);
      notificationService.handleError(error, dispatch, 'Failed to reset invoice');
      setIsLoading(false);
    }
  };

  const handleRefetchInvoiceGroup = async() => {
    await refetchInvoiceGroup();
  };

  const handleCreateLineItem = async() => {
    try {
      if (!invoice) return;
      setIsLoading(true);
      await createLineItem({
        name: `user_created_line_item ${company?.company_name} ${invoice.ext_invoice_number}`,
        invoice_group: invoice.invoice_group,
        invoice: invoice.id,
        attention_needed: true,
      }).unwrap();

      handleRefetchInvoiceGroup();
      setIsLoading(false);
      notificationService.handleSuccess('Line item created successfully', dispatch, 'Line item created successfully');

      // TODO would ideally be able to refetch the invoice and patch the local state
    } catch (error) {
      notificationService.handleError(error, dispatch, 'Failed to create line item');
      handleRefetchInvoiceGroup();
      setIsLoading(false);
    }
  };

  const company = companies?.find((company) => company.id === invoice?.company);

  const bulkApprovePlatformOptions = useMemo((): BulkApprovePlatformOption[] => {
    if (!invoice?.invoice_line_items?.length) {
      return [];
    }
    const map = new Map<string, number>();
    for (const li of invoice.invoice_line_items) {
      if (li.hide || li.status === 'archived') {
        continue;
      }
      if (li.invoice_line_item_status === 'approved') {
        continue;
      }
      const key = (li.platform ?? '').trim() || BULK_APPROVE_EMPTY_PLATFORM_VALUE;
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return Array.from(map.entries())
      .map(([value, count]) => ({
        value,
        label: `${value === BULK_APPROVE_EMPTY_PLATFORM_VALUE ? 'Unknown platform' : value} (${count} pending)`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [invoice]);

  const bulkApproveDisabled =
    isLocked ||
    isLoading ||
    !bulkApproveInvoiceLinesByPlatform ||
    bulkApprovePlatformOptions.length === 0;

  const totalDue = useMemo(
    () => computeInvoiceDrawerTotalDue(invoice ?? null, skus),
    [invoice, skus]
  );

  return (
    <SheetHeader className="border-b pb-2">
      {/* top row */}
      <TooltipProvider>
        <div className='flex flex-row items-center gap-2 justify-between'>
          <div className='flex flex-row items-center gap-2'>
            {invoice?.ext_invoice_number && (
              <div className='flex items-center text-gray-500 pt-1'>
                <Hash className='h-3 w-3' />
                {invoice.ext_invoice_number}
              </div>
            )}
            {isLockedInvoiceStatus(invoice?.invoice_status) && (
              <div className='flex items-center text-gray-500 pt-1'>
                <Lock className='h-4 w-4' />
              </div>
            )}
            <BorderedSelect
              searchEnabled={true}
              disabled={isLocked || isLoading}
              options={companies?.map((company) => ({ value: company.id, label: company.company_name })) || []}
              value={company?.id}
              onChange={(value) => {
                updateInvoice(invoice?.id || '', 'company', value.target.value);
              }}
            />
          </div>
          <div className='flex flex-row items-center gap-2 bg-gray-200 p-1 rounded-full'>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size='med'
                  disabled={isLocked || isLoading}
                  variant='ghost'
                  onClick={() => updateInvoice(invoice?.id || '', 'hide', !invoice?.hide)}
                  className={`flex flex-col hover:bg-gray-201 rounded-none px-4 text-sm gap-1 border-gray-201 rounded-full ${invoice?.hide ? 'bg-gray-700 text-white border-gray-700 hover:border-gray-400 hover:text-black' : 'bg-transparent'}`}
                >
                  {invoice?.hide ? <EyeClosed className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Toggle Invoice Visibility
                </p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size='med'
                  disabled={isLocked || isLoading }
                  variant='ghost'
                  onClick={() => handleResetInvoice()}
                  className={`flex flex-col hover:bg-gray-201 rounded-none px-4 text-sm gap-1 border-gray-201 rounded-full ${isResettingInvoice ? 'animate-spin' : ''}`}
                >
                  <RotateCcw className='h-4 w-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Reset Invoice Group
                </p>
              </TooltipContent>
            </Tooltip>
            {bulkApproveInvoiceLinesByPlatform ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size='med'
                    disabled={bulkApproveDisabled}
                    variant='ghost'
                    onClick={() => setBulkApproveOpen(true)}
                    className="flex flex-col rounded-full border-gray-201 px-4 text-sm gap-1 hover:bg-gray-201"
                    aria-label="Bulk approve by platform"
                  >
                    <CheckCheck className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {bulkApprovePlatformOptions.length === 0
                      ? 'No pending line items to bulk approve'
                      : 'Bulk approve by platform'}
                  </p>
                </TooltipContent>
              </Tooltip>
            ) : null}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size='med'
                  variant='ghost'
                  onClick={(() => handleCreateLineItem())}
                  disabled={isCreatingLineItem || isLocked || isLoading}
                  title='Add Line Item to Invoice'
                  className={`flex flex-col hover:bg-gray-201 rounded-none px-4 text-sm gap-1 border-gray-201 rounded-full ${isLoading ? 'animate-spin' : ''}`}
                >
                  <Plus className='h-4 w-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Add Line Item to Invoice
                </p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size='med'
                  disabled={isLocked || isLoading}
                  variant='ghost'
                  onClick={() => updateInvoice(invoice?.id || '', 'attention_needed', !invoice?.attention_needed)}
                  className={`flex flex-col hover:bg-gray-201 rounded-none px-4 text-sm gap-1 border-gray-201 rounded-full ${invoice?.attention_needed ? 'bg-warning-100 text-warning-800 border-warning-400' : 'bg-transparent'}`}
                >
                  {invoice?.attention_needed ? <FlagOff className='h-4 w-4' /> : <Flag className='h-4 w-4' />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {invoice?.attention_needed ? 'Clear attention needed' : 'Flag attention needed'}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className='mr-5 flex items-center flex-row gap-2'>
            <StatusBadgeThin
              status={invoice?.invoice_status || 'draft'}
              attention_needed={invoice?.attention_needed}
            />
            <div className='text-gray-500 text-sm italic'>
              {invoice?.last_sync ? formatDateTimeVerbose(invoice.last_sync) : ''}
            </div>
          </div>

        </div>

        {/* bottom row */}
        <div className='flex flex-row items-center gap-2 border-gray-201'>
          <div className=''>
            <span className='text-gray-800 text-sm'>
              Total Due
            </span>
            <div className='flex flex-row items-center gap-2 text-2xl font-bold tabular-nums'>
              {formatCurrency(totalDue, 'USD', 2, 2)}
            </div>
          </div>
          <div className='ml-10 flex flex-row items-baseline'>
            <div>
              <div className='flex flex-row items-center gap-2 text-2xl font-bold'>
                <BorderedSelect
                  searchEnabled={true}
                  disabled={isLocked}
                  label='Terms'
                  textClassName='text-sm font-bold'
                  options={terms?.map((term) => ({ value: term.id, label: term.name })) || []}
                  value={invoice?.terms || ''}
                  onChange={(value) => {
                    updateInvoice(invoice?.id || '', 'terms', value.target.value);
                  }}
                />
              </div>
            </div>
            <div className='flex flex-row items-center justify-start'>
              <div className='flex flex-row items-center gap-2 w-full'>
                <BorderDateSelect
                  disabled={isLocked}
                  className='font-bold text-sm'
                  label='Invoice Date'
                  showIcon={false}
                  value={invoice?.invoice_date || ''}
                  onChange={(value) => {
                    updateInvoice(invoice?.id || '', 'invoice_date', value.target.value);
                  }}
                />
              </div>
              <div className='flex flex-row items-center gap-2 w-full'>
                <BorderDateSelect
                  disabled={isLocked}
                  className='font-bold text-sm'
                  label='Due Date'
                  showIcon={false}
                  value={invoice?.due_date || ''}
                  onChange={(value) => {
                    updateInvoice(invoice?.id || '', 'due_date', value.target.value);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </TooltipProvider>

      {bulkApproveInvoiceLinesByPlatform ? (
        <BulkApproveByPlatformModal
          open={bulkApproveOpen}
          onOpenChange={setBulkApproveOpen}
          options={bulkApprovePlatformOptions}
          onSubmit={bulkApproveInvoiceLinesByPlatform}
        />
      ) : null}

    </SheetHeader>
  );
};

export default InvoiceDrawerHeader;
